import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { PaginationProps } from "../../typings";
import { ROWS_PER_PAGE_OPTIONS } from "../../data/pagination";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface AccountsTableProps {
  columns: Column[];
  data: any[];
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  pagination: PaginationProps;
  onPageChange: (e: unknown, page: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AccountsTable: React.FC<AccountsTableProps> = ({
  columns,
  data,
  onEdit,
  onDelete,
  pagination,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState<keyof any>(columns[0].id);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const handleSort =
    (property: keyof any) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 3 }}>
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={handleSort(column.id)}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
              {(onEdit || onDelete) && (
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.colors["white"]["400"],
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <TableCell key={row["id"]}>{row["id"]}</TableCell>
                  <TableCell key={row["account_title"]}>
                    {row["account_title"]}
                  </TableCell>
                  <TableCell key={row["is_system_generated"]}>
                    {row["is_system_generated"] ? "Yes" : "No"}
                  </TableCell>
                  {(onEdit || onDelete) && (
                    <TableCell align="center">
                      {onEdit && (
                        <IconButton
                          aria-label="edit"
                          onClick={() => onEdit(row)}
                          size="small"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          aria-label="delete"
                          onClick={() => onDelete(row)}
                          size="small"
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={pagination.total_no_of_records}
        rowsPerPage={pagination.page_size}
        page={pagination.page_number - 1}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default AccountsTable;
